import { autoinject } from 'aurelia-framework';
import { ComponentBase, IForm, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { AuthService } from 'app/um/services/auth-service';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { Toaster } from 'app/shared/lib/toaster';
import { IPopup } from 'app/shared/elements/popup-base';
import { AddCommonServices } from 'app/um/components/common-codes/add-common-services';
import * as UM from 'app/um/models/essette-api';

@autoinject()
export class CommonServices extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected commonServices: ReadonlyArray<UM.CommonService> | null = null;

    private sorting: Sorting = new Sorting();
    protected resultCount: number | null = 0;
    protected pageSize: number = 25;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;

    protected $addCommonServicesModal!: IPopup;
    protected $form!: IForm;
    protected $commonServicesForm!: Element;
    protected $addCommonServices!: AddCommonServices;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: AuthService,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.reload();
    }

    ////****************** Events ***************************************************////
    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
    }

    protected formSuccess() {
        this.addCommonServicesModalClose();
        this.$addCommonServices.reload();
        this.reload();
    }

    ////****************** Functions ************************************************////
    protected async reload(): Promise<void> {
        await this.loadCommonServices();
    }

    protected addCommonServicesModalClose(): void {
        if(this.$form.reset) this.$form.reset();
        this.$addCommonServicesModal.close();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadCommonServices(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            page: this.$pager?.page || 0,
            top: this.pageSize,
            sorting: this.sorting
        };

        try {
            this.state.beginLoad();
            const response = await this.service.loadCommonServices(options);
            this.commonServices = response.results;
            this.resultCount = response.count;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async deleteCommonService(commonService: UM.CommonService): Promise<void> {
        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.deleteCommonService(commonService);
            isSuccessful = response.isSuccessful;
            this.$addCommonServices.reload();
            this.reload();
        } catch(error) {
            throw error;
        } finally {
            const toast = isSuccessful ? this.toaster.info() : this.toaster.error();
            toast.message({
                key: isSuccessful ? 'ui:messages.events.deletedFromCommonList' : 'ui:messages.events.deleteFromCommonListFailed',
                count: 1,
                what: 'um:commonService.$'
            }).show();
            this.state.endSave();
        }
    }
}
